import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ReactPlayer from 'react-player';

/**
 * @example
 * <VideoPlayer src="https://.../video.mp4" poster="/img/poster.png" controls />
 *
 * @param {import('./lib/propTypes').VideoPlayerProps} props
 */
const VideoPlayer = forwardRef(function VideoPlayer(
  {
    src,
    poster,
    autoPlay = false,
    muted = false,
    controls = true,
    loop = false,
    className = '',
    style = {},
    onAnalytics = () => {},
    width = '100%',
    height = '100%',
    preload = 'auto',
    playsInline = true,
    children
  },
  ref
) {
  const playerRef = useRef(null);
  const visibleRef = useRef(true);
  const watchingRef = useRef(false);
  const lastPerfRef = useRef(null);
  const watchedMsRef = useRef(0);
  const [ready, setReady] = useState(false);

  // expose a simple imperative handle so parent components can call player functions
  useImperativeHandle(
    ref,
    () => ({
      getInternalPlayer: () => playerRef.current,
      snapshot: (extra = {}) => snapshot(extra)
    }),
    []
  );

  // Tab visibility tracking
  useEffect(() => {
    const onVis = () => {
      visibleRef.current = document.visibilityState === 'visible';
      emit('visibility', { visible: visibleRef.current });
    };
    onVis();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    window.addEventListener('blur', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
      window.removeEventListener('blur', onVis);
    };
  }, []);

  // Precise watch-time accumulation using requestAnimationFrame
  useEffect(() => {
    let rafId;
    const step = () => {
      const player = playerRef.current;
      if (player && watchingRef.current && visibleRef.current) {
        const now = performance.now();
        if (lastPerfRef.current != null) {
          watchedMsRef.current += now - lastPerfRef.current;
        }
        lastPerfRef.current = now;
      } else {
        lastPerfRef.current = null;
      }
      rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // Heartbeat every 5s + unload beacon
  useEffect(() => {
    const id = setInterval(() => emit('heartbeat'), 5000);
    const unload = () => beacon('unload');
    window.addEventListener('beforeunload', unload);
    return () => {
      clearInterval(id);
      window.removeEventListener('beforeunload', unload);
    };
  }, []);

  function snapshot(extra = {}) {
    const player = playerRef.current;
    const currentTime = player?.getCurrentTime?.() ?? 0;
    const duration = player?.getDuration?.() ?? 0;
    return {
      currentTime,
      duration,
      watchedMs: Math.round(watchedMsRef.current),
      visible: visibleRef.current,
      ts: Date.now(),
      ...extra
    };
  }

  function emit(type, extra = {}) {
    try {
      onAnalytics({ type, ...snapshot(extra) });
    } catch (e) {
      // Swallow analytics errors to avoid breaking playback
      // eslint-disable-next-line no-console
      console.error('VideoPlayer analytics error', e);
    }
  }

  function beacon(type, extra = {}) {
    try {
      const payload = JSON.stringify({ type, ...snapshot(extra) });
      navigator.sendBeacon?.('/analytics/video', payload);
    } catch (e) {
      // ignore
    }
  }

  return (
    <div
      className={`aspect-video w-full max-w-4xl overflow-hidden rounded-2xl ${className}`}
      style={style}
      aria-hidden={false}>
      <ReactPlayer
        ref={playerRef}
        src={src}
        playing={autoPlay}
        muted={muted}
        controls={controls}
        loop={loop}
        width={width}
        height={height}
        config={{
          file: {
            attributes: {
              poster,
              preload,
              playsinline: playsInline,
              crossOrigin: 'anonymous'
            }
          }
        }}
        onReady={() => {
          setReady(true);
          emit('ready');
        }}
        onPlay={() => {
          watchingRef.current = true;
          emit('play');
        }}
        onPause={() => {
          watchingRef.current = false;
          emit('pause');
        }}
        onEnded={() => {
          watchingRef.current = false;
          emit('ended');
        }}
        onProgress={() => ready && emit('timeupdate')}
        onSeek={() => emit('seek')}
        onBuffer={() => emit('buffer')}
        onBufferEnd={() => emit('bufferend')}
        onError={(e) => emit('error', { error: String(e) })}
      />
      {children}
    </div>
  );
});

export default VideoPlayer;
